import { dungApiThat, goiApi } from '../api/http';
import { mayChuCapThemToken } from '../../mock/cap-them-token';
import { mockRequest, mockState } from '../../mock/transport';
import type {
  AiTokenGrant,
  AiTokenGrantCreate,
  AiTokenRequest,
  AiTokenRequestApprove,
  AiTokenRequestCreate,
  AiTokenRequestQueue,
  AiTokenRequestStatus,
} from '../../modules/tenant/types';

/**
 * Token AI cấp thêm và yêu cầu cấp thêm (ADR-0091, tenant-service, tag `ai-token-grants` của
 * `openapi/platform-api.yaml`).
 *
 * - Đọc dòng cấp thêm, yêu cầu của một tenant: `PLATFORM_ADMIN`, hoặc `TENANT_ADMIN` của chính tenant đó.
 * - Cấp thêm, gia hạn, thu hồi, hàng chờ, duyệt, từ chối: chỉ `PLATFORM_ADMIN`.
 * - Gửi và huỷ yêu cầu: chỉ `TENANT_ADMIN` của tenant đó (`tenant_id` từ ngữ cảnh đăng nhập).
 *
 * Mỗi hàm chọn **lúc gọi** giữa API thật và máy chủ giả (`mock/cap-them-token.ts`). Danh sách của một tenant
 * và hàng chờ nhỏ (tối đa vài chục dòng), nên đọc hết các trang một lần: màn không phải dựng nút "Tải thêm"
 * cho dữ liệu mà người duyệt cần thấy cả.
 */
const goc = (tenantId: string) => `/api/v1/tenants/${encodeURIComponent(tenantId)}`;
const HANG_CHO = '/api/v1/security/ai-token-requests';

/** Máy chủ giả chạy **sau** nhịp chờ và các trạng thái `?mock=`: lỗi tới cùng độ trễ với lỗi thật. */
const giaLap = <T>(lam: () => T): Promise<T> => mockRequest(undefined).then(lam);

interface Trang<T> {
  items: T[];
  next_cursor: string | null;
}

/** Đọc hết các trang của một danh sách cursor (tối đa 10 trang × 100 dòng, đủ cho hàng chờ tay). */
async function docHet<T, R extends Trang<T>>(duongDan: string, query: Record<string, string> = {}): Promise<R> {
  let cursor: string | null = null;
  let dau: R | null = null;
  const items: T[] = [];
  for (let i = 0; i < 10; i++) {
    const q = new URLSearchParams({ ...query, limit: '100', ...(cursor ? { cursor } : {}) });
    const trang: R = await goiApi<R>(`${duongDan}?${q.toString()}`);
    dau ??= trang;
    items.push(...trang.items);
    cursor = trang.next_cursor;
    if (!cursor) break;
  }
  return { ...(dau as R), items, next_cursor: cursor };
}

export const capThemTokenRepository = {
  /** `GET /api/v1/tenants/{id}/ai-token-grants` — mới nhất trước, gồm cả đã hết hạn, đã thu hồi. */
  capThemCuaTenant: (tenantId: string): Promise<AiTokenGrant[]> =>
    dungApiThat()
      ? docHet<AiTokenGrant, Trang<AiTokenGrant>>(`${goc(tenantId)}/ai-token-grants`).then((t) => t.items)
      : giaLap(() => (mockState() === 'empty' ? [] : mayChuCapThemToken.capThemCua(tenantId).items)),

  /** `POST /api/v1/tenants/{id}/ai-token-grants` — nạp thêm hoặc gói tạm thời, có hiệu lực ngay. */
  taoCapThem: (tenantId: string, du: AiTokenGrantCreate, khoaLap: string): Promise<AiTokenGrant> =>
    dungApiThat()
      ? goiApi<AiTokenGrant>(`${goc(tenantId)}/ai-token-grants`, { method: 'POST', body: du, idempotencyKey: khoaLap })
      : giaLap(() => mayChuCapThemToken.taoCapThem(tenantId, du)),

  /** `POST …/ai-token-grants/{grant_id}/extend` — chỉ gói tạm thời. */
  giaHan: (tenantId: string, grantId: string, du: { valid_to: string; note?: string | null }): Promise<AiTokenGrant> =>
    dungApiThat()
      ? goiApi<AiTokenGrant>(`${goc(tenantId)}/ai-token-grants/${encodeURIComponent(grantId)}/extend`, {
          method: 'POST',
          body: du,
        })
      : giaLap(() => mayChuCapThemToken.giaHan(tenantId, grantId, du.valid_to, du.note)),

  /** `POST …/ai-token-grants/{grant_id}/revoke` — không hoàn tác. */
  thuHoi: (tenantId: string, grantId: string, reason?: string | null): Promise<AiTokenGrant> =>
    dungApiThat()
      ? goiApi<AiTokenGrant>(`${goc(tenantId)}/ai-token-grants/${encodeURIComponent(grantId)}/revoke`, {
          method: 'POST',
          body: { reason: reason ?? null },
        })
      : giaLap(() => mayChuCapThemToken.thuHoi(tenantId, grantId)),

  /** `GET /api/v1/tenants/{id}/ai-token-requests` — mới nhất trước. */
  yeuCauCuaTenant: (tenantId: string): Promise<AiTokenRequest[]> =>
    dungApiThat()
      ? docHet<AiTokenRequest, Trang<AiTokenRequest>>(`${goc(tenantId)}/ai-token-requests`).then((t) => t.items)
      : giaLap(() => (mockState() === 'empty' ? [] : mayChuCapThemToken.yeuCauCua(tenantId).items)),

  /** `POST /api/v1/tenants/{id}/ai-token-requests` — Tenant Admin gửi; tối đa 3 yêu cầu đang chờ. */
  guiYeuCau: (tenantId: string, du: AiTokenRequestCreate, khoaLap: string): Promise<AiTokenRequest> =>
    dungApiThat()
      ? goiApi<AiTokenRequest>(`${goc(tenantId)}/ai-token-requests`, {
          method: 'POST',
          body: du,
          idempotencyKey: khoaLap,
        })
      : giaLap(() => mayChuCapThemToken.guiYeuCau(tenantId, du)),

  /** `POST …/ai-token-requests/{request_id}/cancel` — chỉ yêu cầu đang chờ. */
  huyYeuCau: (tenantId: string, requestId: string): Promise<AiTokenRequest> =>
    dungApiThat()
      ? goiApi<AiTokenRequest>(`${goc(tenantId)}/ai-token-requests/${encodeURIComponent(requestId)}/cancel`, {
          method: 'POST',
        })
      : giaLap(() => mayChuCapThemToken.huyYeuCau(tenantId, requestId)),

  /**
   * `GET /api/v1/security/ai-token-requests` — hàng chờ của mọi tenant. `pending_count` không theo bộ lọc.
   * `?mock=filtered-empty` diễn bộ lọc không còn dòng nào khi đang lọc.
   */
  hangCho: (status: AiTokenRequestStatus | undefined): Promise<AiTokenRequestQueue> =>
    dungApiThat()
      ? docHet<AiTokenRequest, AiTokenRequestQueue>(HANG_CHO, status ? { status } : {})
      : giaLap(() => {
          const st = mockState();
          const kq = mayChuCapThemToken.hangCho(status);
          if (st === 'empty') return { items: [], next_cursor: null, pending_count: 0 };
          if (st === 'filtered-empty' && status) return { ...kq, items: [] };
          return kq;
        }),

  /** `POST …/ai-token-requests/{request_id}/approve` — sinh hoặc gia hạn dòng cấp thêm cùng một bước. */
  duyet: (requestId: string, du: AiTokenRequestApprove): Promise<AiTokenRequest> =>
    dungApiThat()
      ? goiApi<AiTokenRequest>(`${HANG_CHO}/${encodeURIComponent(requestId)}/approve`, { method: 'POST', body: du })
      : giaLap(() => mayChuCapThemToken.duyet(requestId, du)),

  /** `POST …/ai-token-requests/{request_id}/reject` — `note` gửi tới Tenant Admin. */
  tuChoi: (requestId: string, note: string): Promise<AiTokenRequest> =>
    dungApiThat()
      ? goiApi<AiTokenRequest>(`${HANG_CHO}/${encodeURIComponent(requestId)}/reject`, {
          method: 'POST',
          body: { note },
        })
      : giaLap(() => mayChuCapThemToken.tuChoi(requestId, note)),
};
